import { type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "../../lib/utils";

export interface SectionNavItem<K extends string> {
  key: K;
  label: string;
  icon: LucideIcon;
  /** Optional count rendered as a pill beside the label (e.g. pending items). */
  badge?: number;
}

interface SectionNavProps<K extends string> {
  items: SectionNavItem<K>[];
  value: K;
  onChange: (key: K) => void;
}

/**
 * Section navigation for a detail page. Responsive by design: a vertical list
 * on desktop (`md+`) that sits alongside the content, becoming a centered
 * floating bottom bar on narrow screens. Buttons carry `tab` semantics so
 * assistive tech announces the selected section.
 */
export function SectionNav<K extends string>({
  items,
  value,
  onChange,
}: SectionNavProps<K>): ReactNode {
  const reduceMotion = useReducedMotion();

  return (
    <motion.nav
      role="tablist"
      initial={reduceMotion ? false : { opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={cn(
        "fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] left-1/2 z-40 flex w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 gap-1 rounded-xl border border-outline-variant/40 bg-surface-container-lowest/95 p-2 shadow-xl backdrop-blur-md",
        "md:static md:w-auto md:max-w-none md:translate-x-0 md:flex-col md:border-0 md:bg-transparent md:p-0 md:shadow-none md:backdrop-blur-none",
      )}
    >
      {items.map((item) => {
        const active = item.key === value;
        const Icon = item.icon;
        return (
          <motion.button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.key)}
            whileTap={reduceMotion ? undefined : { scale: 0.98 }}
            className={cn(
              "relative inline-flex min-w-0 basis-0 flex-1 cursor-pointer flex-col items-center gap-1 rounded-md px-1.5 py-2 text-[10px] leading-3 font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
              "md:w-full md:flex-row md:justify-start md:gap-2 md:rounded-full md:px-4 md:text-label-md",
              active
                ? "text-primary"
                : "text-on-surface-variant hover:bg-surface-container/60 hover:text-on-surface",
            )}
          >
            {active ? (
              <motion.span
                layoutId="section-nav-active"
                className="absolute inset-0 rounded-md bg-surface-container md:rounded-full"
                transition={{ type: "spring", stiffness: 420, damping: 34 }}
              />
            ) : null}
            <motion.span
              animate={
                reduceMotion
                  ? undefined
                  : { scale: active ? 1.06 : 1, y: active ? -1 : 0 }
              }
              transition={{ type: "spring", stiffness: 420, damping: 30 }}
              className="relative z-10"
            >
              <Icon className="size-5 shrink-0 md:size-4" />
            </motion.span>
            <span className="relative z-10 truncate">{item.label}</span>
            {item.badge ? (
              <motion.span
                layout
                className={cn(
                  "absolute top-1 right-1 z-10 inline-flex size-4 items-center justify-center rounded-full text-[9px] leading-none md:static md:ml-auto md:h-5 md:min-w-5 md:px-1.5 md:text-label-sm",
                  active
                    ? "bg-primary text-on-primary"
                    : "bg-primary/15 text-primary",
                )}
              >
                {item.badge}
              </motion.span>
            ) : null}
          </motion.button>
        );
      })}
    </motion.nav>
  );
}
